// Regras da página "Endereços a corrigir" (pages/clients/EnderecosPage.tsx) e do selo "🚩 N pendências de
// cadastro" da ClientsPage. Moram aqui, fora das telas, pelo mesmo motivo de `navegacaoCliente.ts`: o erro
// é SILENCIOSO — nada quebra, o comercial só corrige na ordem errada, ou deixa de ver um cliente — e aqui
// roda em jest (`src/utils/__tests__/pendenciasEndereco.test.ts`).
//
// Quem grava a pendência é o SERVIDOR (CNEFE 2022 do IBGE, regra 'cnefe2022-v2'): `geoPendencia` + o espelho
// `geoPendente: true`, que existe só para a consulta where('geoPendente','==',true) — índice de campo único,
// sem índice composto; filtro e ordem acontecem aqui, no navegador. O painel só LÊ: o comercial corrige no
// Sankhya e a sincronização traz a correção, e aí a pendência some sozinha.
import {
  Client, GeoPendencia, GeoPendenciaTipo, cadastroFieldLabel, cadastroIssueLabel, geoPendenciaMotivoLabel,
  geoPendenciaTipoLabel,
} from '../types';

/** Ordem da fila: coordenada que leva a lugar errado primeiro, depois endereço que não resolve, depois cidade. */
export const ORDEM_TIPOS: GeoPendenciaTipo[] = ['geoSuspeita', 'geoNaoLocalizado', 'cidadeInvalida'];

// Firestore devolve Timestamp; teste/seed pode trazer Date. Mesmo helper do LiveMapPage/RotasPage.
export const toDateAny = (v: any): Date | null => (v?.toDate ? v.toDate() : v instanceof Date ? v : null);

/**
 * A pendência de endereço do cliente, ou null. Só conta objeto com `tipo` em texto: um doc com
 * `geoPendente: true` e sem `geoPendencia` (o servidor promete que não existe) não tem o que mostrar na
 * coluna "O que corrigir" e fica de fora em vez de virar uma linha vazia.
 */
export function pendenciaDe(c: Pick<Client, 'geoPendencia'> | null | undefined): GeoPendencia | null {
  const p = c?.geoPendencia;
  return p && typeof p === 'object' && typeof p.tipo === 'string' && p.tipo ? p : null;
}

// Tipo que o painel ainda não conhece (servidor mais novo) vai para o FIM, com o código cru como rótulo —
// some da ordem, mas não some da fila.
const rankDoTipo = (tipo: string | undefined) => {
  const i = ORDEM_TIPOS.indexOf(tipo as GeoPendenciaTipo);
  return i < 0 ? ORDEM_TIPOS.length : i;
};
const rankTipo = (c: Pick<Client, 'geoPendencia'>) => rankDoTipo(pendenciaDe(c)?.tipo);
// "Nunca visitado" = -1: vai para o fim do tipo na ordem decrescente (e não gera NaN no comparador).
const visitaMs = (c: Pick<Client, 'lastCheckinAt'>) => toDateAny(c.lastCheckinAt)?.getTime() ?? -1;
const codparcNum = (c: Pick<Client, 'sankhya'>) => {
  const n = Number(c.sankhya?.codparc);
  return Number.isFinite(n) && n > 0 ? n : Number.MAX_SAFE_INTEGER; // sem CODPARC vai para o fim
};
// Comparador sem subtração: Infinity - Infinity = NaN embaralha o sort sem erro nenhum.
const cmp = (x: number, y: number) => (x < y ? -1 : x > y ? 1 : 0);

/** geoSuspeita → geoNaoLocalizado → cidadeInvalida; dentro do tipo, visita mais recente primeiro. */
export function ordenarPendencias<T extends Pick<Client, 'geoPendencia' | 'lastCheckinAt' | 'sankhya'>>(lista: T[]): T[] {
  return [...lista].sort((a, b) =>
    cmp(rankTipo(a), rankTipo(b))
    || cmp(visitaMs(b), visitaMs(a))
    || cmp(codparcNum(a), codparcNum(b)));
}

/** Sem acento e minúsculo — "sao jose" acha "SÃO JOSÉ". */
const normBusca = (s: unknown) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export const ufDe = (c: Pick<Client, 'address'>) => String(c.address?.state || '').trim().toUpperCase();
export const vendedorDe = (c: Pick<Client, 'sellerName'>) => String(c.sellerName || '').trim();

export interface FiltroPendencias {
  busca: string;    // nome (razão social ou fantasia), código (CODPARC) ou cidade
  motivo: string;   // '' = todos
  vendedor: string; // '' = todos (compara o `sellerName` como aparece na coluna)
  uf: string;       // '' = todas
}

export function filtrarPendencias<T extends Client>(lista: T[], f: FiltroPendencias): T[] {
  const q = normBusca(f.busca);
  return lista.filter((c) => {
    if (f.motivo && pendenciaDe(c)?.motivo !== f.motivo) return false;
    if (f.vendedor && vendedorDe(c) !== f.vendedor) return false;
    if (f.uf && ufDe(c) !== f.uf) return false;
    if (!q) return true;
    return normBusca(c.name).includes(q)
      || normBusca(c.tradeName).includes(q)
      || normBusca(c.address?.city).includes(q)
      || String(c.sankhya?.codparc ?? '').includes(q);
  });
}

/** Contagem por tipo (os três cartões). Tipo desconhecido não entra em cartão nenhum. */
export function contarPorTipo(lista: Pick<Client, 'geoPendencia'>[]): Record<GeoPendenciaTipo, number> {
  const n: Record<GeoPendenciaTipo, number> = { geoSuspeita: 0, geoNaoLocalizado: 0, cidadeInvalida: 0 };
  for (const c of lista) {
    const t = pendenciaDe(c)?.tipo;
    if (t && ORDEM_TIPOS.includes(t)) n[t]++;
  }
  return n;
}

/** Opções dos selects: só o que existe na fila. Motivos agrupados por tipo, na ordem da fila. */
export function opcoesDosFiltros(lista: Client[]): {
  motivos: { tipo: string; motivos: string[] }[];
  vendedores: string[];
  ufs: string[];
} {
  const porTipo = new Map<string, Set<string>>();
  const vendedores = new Set<string>();
  const ufs = new Set<string>();
  for (const c of lista) {
    const p = pendenciaDe(c);
    if (p?.motivo) {
      if (!porTipo.has(p.tipo)) porTipo.set(p.tipo, new Set());
      porTipo.get(p.tipo)!.add(p.motivo);
    }
    const v = vendedorDe(c);
    if (v) vendedores.add(v);
    const uf = ufDe(c);
    if (uf) ufs.add(uf);
  }
  const tipos = Array.from(porTipo.keys()).sort((a, b) => cmp(rankDoTipo(a), rankDoTipo(b)));
  const ptBR = (a: string, b: string) => a.localeCompare(b, 'pt-BR');
  return {
    motivos: tipos.map((tipo) => ({
      tipo,
      motivos: Array.from(porTipo.get(tipo)!).sort((a, b) => ptBR(geoPendenciaMotivoLabel(a), geoPendenciaMotivoLabel(b))),
    })),
    vendedores: Array.from(vendedores).sort(ptBR),
    ufs: Array.from(ufs).sort(ptBR),
  };
}

/** "logradouro, número · cidade/UF", só com as partes que existem — como está no Sankhya, sem limpeza. */
export function linhaEndereco(a: Partial<Client['address']> | null | undefined): string {
  const logradouro = [String(a?.street || '').trim(), String(a?.number || '').trim()].filter(Boolean).join(', ');
  const cidadeUf = [String(a?.city || '').trim(), String(a?.state || '').trim()].filter(Boolean).join('/');
  return [logradouro, cidadeUf].filter(Boolean).join(' · ');
}

/** Última visita em dd/mm, ou "nunca". */
export function ultimaVisitaCurta(v: unknown): string {
  const d = toDateAny(v);
  return d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : 'nunca';
}

/**
 * Selo "🚩 N pendências de cadastro" da ClientsPage: as pendências da verificação de cadastro (como
 * sempre: só com `cadastroPending`) SOMADAS à pendência de endereço, quando houver. A dica (title) lista
 * cada uma; a de endereço vai como "rótulo do tipo: detalhe". null = sem selo.
 */
export function seloPendencias(c: Pick<Client, 'cadastroPending' | 'cadastroIssues' | 'cadastroMissingFields' | 'geoPendencia'>):
  { total: number; dica: string } | null {
  // O código de TIPO de pendência de endereço que a verificação de cadastro também anota em `cadastroIssues`
  // (functions/index.js, `avaliarPendenciasCliente`) fica de fora: a pendência de endereço entra pela `geoPendencia`
  // VIVA, logo abaixo — contar os dois dobraria, e o código da verificação é a foto da última rodada (pode ter sido
  // resolvido no Sankhya depois).
  const issues = (c.cadastroPending ? (c.cadastroIssues || []) : []).filter((code) => !ORDEM_TIPOS.includes(code as GeoPendenciaTipo));
  const partes = issues.map((code) =>
    code === 'missingFields' && (c.cadastroMissingFields || []).length
      ? `Campos faltando: ${(c.cadastroMissingFields || []).map(cadastroFieldLabel).join(', ')}`
      : cadastroIssueLabel(code));
  const geo = pendenciaDe(c);
  if (geo) {
    const detalhe = String(geo.detalhe || '').trim();
    partes.push(detalhe ? `${geoPendenciaTipoLabel(geo.tipo)}: ${detalhe}` : geoPendenciaTipoLabel(geo.tipo));
  }
  return partes.length ? { total: partes.length, dica: partes.join(' • ') } : null;
}

/** Linhas da planilha exportada: o que está na tela (filtros e ordem), com o endereço inteiro do cadastro. */
export function linhasParaExportar(lista: Client[]): Record<string, string | number>[] {
  return lista.map((c) => {
    const p = pendenciaDe(c);
    const d = toDateAny(c.lastCheckinAt);
    return {
      'Código': c.sankhya?.codparc != null && c.sankhya.codparc !== '' ? Number(c.sankhya.codparc) || String(c.sankhya.codparc) : '',
      'Razão social': c.name || '',
      'Nome fantasia': c.tradeName || '',
      'Logradouro': c.address?.street || '',
      'Número': c.address?.number || '',
      'Bairro': c.address?.neighborhood || '',
      'Cidade': c.address?.city || '',
      'UF': c.address?.state || '',
      'CEP': c.address?.zipCode || '',
      'Pendência': geoPendenciaTipoLabel(p?.tipo),
      'Motivo': geoPendenciaMotivoLabel(p?.motivo),
      'O que corrigir no Sankhya': p?.detalhe || '',
      'Distância (km)': typeof p?.distKm === 'number' && Number.isFinite(p.distKm) ? Math.round(p.distKm * 10) / 10 : '',
      'Vendedor': c.sellerName || '',
      'Última visita': d ? d.toLocaleDateString('pt-BR') : 'nunca',
    };
  });
}
